/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import useAnimatedWrapper from '../../hooks/useAnimatedWrapper';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import Tooltip from '../tooltip/Tooltip';

export type PopupInputProps = {
  index: number;
  label: string;
  value: string;
  active: boolean;
};

export const useStyles = makeStyles((theme: DLSTheme) => ({
  inputStatusBarContainer : {
    display        : 'flex',
    flexDirection  : 'column',
    alignItems     : 'center',
    justifyContent : 'center',
    height         : '20px',
    margin         : '0px 4px 0px 0px'
  },
  bar : {
    backgroundColor : ({active}: {active: boolean}) => active ? dlsColor('caBlue') : dlsColor('white'),
    boxSizing       : 'border-box',
    borderRadius    : '2px',
    border          : `2px solid ${dlsColor('caBlue')}`,
    padding         : '2px 4px',
    width           : '24px',
    textAlign       : 'center'
  },
  inputLabel : {
    ...dlsAlt('typography', 'label3'),
    color : dlsColor('grey2')
  }
}));

/**
 * @PopupInputs
 * 
 * This component is used in the DeviceInputContent component to display the input status bar.
 * Each input bar is animated and is staggered based on the index of the input.
 * 
 * When the input is active, the bar is blue and when inactive, the bar is white with a blue border.
 * 
 * @param index - The index of the input.
 * @param label - The label of the input.
 * @param value - The value of the input.
 * @param active - The active status of the input.
 * 
 * @returns A component that displays the input status bar containing each input's label and value.
 * 
 * @example
 * ```typescript
 * import PopupInputs from '@ion/dls/src/components/inputs/PopupInputs';
 * 
 * <Popup>
 *  ...
 *   <PopupInputs index={index} label={label} value={value} active={active} />
 * </Popup>
 * 
 * ```
 * 
 */
const PopupInputs = ({
  index,
  label,
  value,
  active
}: PopupInputProps) => {
  const classes         = useStyles({active});
  const AnimatedWrapper = useAnimatedWrapper({fromLeft: true, staggered: true, index});

  return (
    <AnimatedWrapper key={index}>
      <Tooltip title={`${label} ${value}`} placement='bottom' arrow>
        <div className={classes.inputStatusBarContainer}>
          <div className={classes.bar} />
          <span className={classes.inputLabel}>
            {index + 1}
          </span>
        </div>
      </Tooltip>
    </AnimatedWrapper>
  );
};

export default PopupInputs;
